import { z } from 'zod';
import { exposureWindowSchema } from './schemas.js';
/**
 * Typisierter Settings-Store (DECISIONS.md D-21). §15 verlangt, dass alle Startwerte,
 * Gewichte und Schwellen änderbar sind — ein untypisierter Key/Value-Store würde ein
 * Scoring-Gewicht als String "0.35" akzeptieren und still falsch rechnen.
 *
 * Jeder Key steht hier mit Schema UND Default. Der Default ist die Antwort auf
 * "was gilt, solange nichts gesetzt wurde" — es gibt keinen zweiten Ort dafür.
 */
export const SETTINGS_SPEC = {
    /** §4 Invariante 7: der Tag läuft von 04:00 bis 04:00 lokaler Zeit. */
    day_cutoff_hour: { schema: z.number().int().min(0).max(23), default: 4 },
    home_lat: { schema: z.number().min(-90).max(90), default: 52.52 },
    home_lon: { schema: z.number().min(-180).max(180), default: 13.405 },
    /** D-02: der Innen-Bucket braucht eine Temperatur, sonst zieht Außenkälte ihn falsch. */
    indoor_temp_c: { schema: z.number().min(0).max(35), default: 21 },
    indoor_weight: { schema: z.number().min(0).max(1), default: 0.15 },
    /** §5 Default-Fenster. Summe der Fenstergewichte + indoor_weight muss 1.0 ergeben. */
    exposure_windows: {
        schema: z.array(exposureWindowSchema).min(1),
        default: [
            { label: 'morning', from: '07:30', to: '08:30', weight: 0.35 },
            { label: 'noon', from: '12:00', to: '13:00', weight: 0.15 },
            { label: 'evening', from: '17:30', to: '19:00', weight: 0.35 },
        ],
    },
    /** Abendtermin: Erinnerung an ausstehendes Feedback — nur, wenn wirklich etwas offen ist. */
    push_time: { schema: z.string().regex(/^\d{2}:\d{2}$/), default: '20:30' },
    push_enabled: { schema: z.boolean(), default: true },
    /** §8: Kill-Switch. Default aus — kein Bild verlässt den Pi ungefragt. */
    gemini_enabled: { schema: z.boolean(), default: false },
    gemini_model: { schema: z.string().min(1), default: 'gemini-3.5-flash-lite' },
    /**
     * §6 Schritt 2: Stützstellen der Kennlinie f(outfit_warmth) → comfort_center_c.
     * PRAGMATISCHE SCHÄTZUNGEN, nicht empirisch validiert (siehe DECISIONS.md D-01).
     * Das physikalische Pendant wären clo-Werte nach ISO 9920, die hier nicht vorliegen.
     * Monoton fallend: mehr Wärmeleistung → passt zu niedrigerer Außentemperatur.
     *
     * D-39: Die Stützstellen sind neu verankert, weil `warmth_weights` die Summen anders
     * bildet. Fünf Referenz-Outfits (Sommer, Übergang, Büro, Kalt, Winter) behalten dabei
     * ihr bisheriges Komfortzentrum — ein Test hält die fünf Paare fest.
     */
    warmth_curve: {
        schema: z.array(z.object({ warmth: z.number(), centerC: z.number() })).min(2),
        default: [
            { warmth: 0, centerC: 30 },
            { warmth: 3, centerC: 21 },
            { warmth: 7, centerC: 13 },
            { warmth: 10, centerC: 10 },
            { warmth: 15, centerC: 4 },
            { warmth: 19, centerC: -1 },
            { warmth: 25, centerC: -8 },
            { warmth: 32, centerC: -16 },
        ],
    },
    /**
     * §6 Schritt 2, D-39: Gewichte je Körperzone.
     *
     * Abnehmender Grenznutzen gilt für **gestapelte** Schichten: der zweite Pullover wärmt
     * weniger als der erste. Für Hose und Schuhe gilt er nicht — man trägt genau eine Hose,
     * und sie steht nicht in Konkurrenz zum Mantel. Vorher lief eine einzige Rangfolge über
     * alle Teile, weshalb bei T-Shirt + Shorts + Sneaker die Schuhe das volle Gewicht
     * bekamen und das T-Shirt 0,7.
     *
     * `torso` gilt der Reihe nach für base/mid/outer, nach Wärmepunkten absteigend.
     */
    warmth_weights: {
        schema: z.object({
            torso: z.array(z.number().min(0).max(1)).min(1),
            bottom: z.number().min(0).max(1),
            footwear: z.number().min(0).max(1),
            accessory: z.number().min(0).max(1),
        }),
        default: { torso: [1.0, 0.7, 0.5, 0.3], bottom: 1.0, footwear: 0.5, accessory: 0.25 },
    },
    /** §6 Schritt 3. Alle Terme sind Abwertungen (negativ), außer Rotation und Outfit-Bonus. */
    scoring_weights: {
        schema: z.object({
            thermal: z.number(),
            rain: z.number(),
            wind: z.number(),
            formality: z.number(),
            recency: z.number(),
            rotation: z.number(),
            feedback: z.number(),
            savedOutfitBonus: z.number(),
        }),
        default: {
            thermal: 1.0,
            rain: 6.0,
            wind: 3.0,
            formality: 1.5,
            recency: 2.0,
            rotation: 1.0,
            feedback: 1.5,
            savedOutfitBonus: 0.75,
        },
    },
    /*
     * Die Zielformalität je Anlass stand hier einmal als `occasion_formality`. Seit D-34
     * sind Anlässe Daten: Beschriftung UND Zielformalität stehen in der Tabelle
     * `occasions`, und `RecommendService.occasionOf` liest sie von dort. Die Einstellung
     * blieb danach als zweite, von niemandem gelesene Wahrheit stehen — genau das, was
     * D-34 verhindern wollte.
     */
    /** Wie weit ein Teil unter dem Zielniveau liegen darf, bevor es hart gefiltert wird. */
    formality_tolerance: { schema: z.number().int().min(0).max(4), default: 2 },
    /**
     * D-39: Wie lange eine Rückmeldung im Scoring nachwirkt. Ohne Grenze bestrafte der
     * Feedback-Term ein Teil ewig für einen Fehlgriff, den die Lernschleife längst
     * ausgeglichen hat — die Korrektur wirkte, die Strafe blieb.
     */
    feedback_memory_days: { schema: z.number().int().min(1).max(3650), default: 90 },
    rain_risk_threshold: { schema: z.number().min(0).max(1), default: 0.4 },
    wind_threshold_kmh: { schema: z.number().min(0), default: 25 },
    recency_days: { schema: z.number().int().min(0).max(90), default: 7 },
    /** §7 Schritt 4. */
    learning_alpha: { schema: z.number().min(0).max(1), default: 0.15 },
    learning_clip_c: { schema: z.number().min(0).max(15), default: 3.0 },
    /** Kelvin je Stufe der 7-stufigen Skala (§7). Schätzung, änderbar. */
    learning_kelvin_per_step: { schema: z.number().min(0).max(10), default: 1.5 },
    /** Anteil des Fehlers, der auf den globalen Bias statt aufs Item geht (§7 Schritt 2). */
    learning_bias_share: { schema: z.number().min(0).max(1), default: 0.3 },
    /** Shrinkage n/(n+k): solange wenige Beobachtungen, zieht der Wert zum Prior. */
    learning_shrink_k: { schema: z.number().min(0).max(50), default: 5 },
    /** Gelernt, nicht gesetzt — steht hier, weil es global ist und kein Item hat (D-23). */
    user_thermal_bias: { schema: z.number().min(-10).max(10), default: 0 },
    /**
     * D-38 Pflege-Lernschleife. Drei Beobachtungen sind wenig — aber der Median über drei
     * eigene Beobachtungen ist immer noch näher an der Wahrheit als der Mittelwert einer
     * Kategorie, den niemand gemessen hat.
     */
    care_learning_min_obs: { schema: z.number().int().min(2).max(20), default: 3 },
    /** Wie viele der jüngsten Beobachtungen in den Median eingehen. */
    care_learning_window: { schema: z.number().int().min(2).max(50), default: 5 },
    /** §6 Schritt 4: Konventionen, keine Wahrheiten. Abschaltbar, nie unterdrückend. */
    color_warnings_enabled: { schema: z.boolean(), default: true },
    backup_generations: { schema: z.number().int().min(1).max(60), default: 7 },
    /** §7: Feedback nachtragbar bis 48 h, danach schreibgeschützt. */
    feedback_grace_hours: { schema: z.number().int().min(1).max(720), default: 48 },
    /** D-08: Abstand des Wetter-Nachlaufs; 36 h liegt sicher hinter dem Tagesende. */
    weather_backfill_hours: { schema: z.number().int().min(1).max(240), default: 36 },
    /**
     * D-27: Wie lange eine geholte Prognose als frisch gilt. 30 min ist ein Kompromiss —
     * Open-Meteo aktualisiert stündlich, und die Morgens-Ansicht soll nicht bei jedem
     * Öffnen am Netz hängen (§1: unter zwei Sekunden nutzbar).
     */
    weather_cache_minutes: { schema: z.number().int().min(0).max(1440), default: 30 },
    /** §10: Aussortier-Radar. */
    unworn_alert_months: { schema: z.number().int().min(1).max(60), default: 12 },
    /**
     * §11: Benachrichtigungen. Bewusst wenige und feste Anlässe — eine App, die von sich
     * aus redet, verliert Vertrauen schneller als eine, die schweigt.
     * Der Abendtermin steht schon oben als `push_time` (Feedback-Erinnerung).
     */
    push_morning_time: { schema: z.string().regex(/^\d{2}:\d{2}$/), default: '07:00' },
    /** Meldung, sobald Teile aus dem Trocknen kommen. */
    push_laundry_enabled: { schema: z.boolean(), default: true },
    /** Vorlauf in Tagen, bevor an eine Abholung bei der Reinigung erinnert wird. */
    care_reminder_lead_days: { schema: z.number().int().min(0).max(30), default: 0 },
    /**
     * §9 Packmodus. Alles drei sind Faustregeln, keine Messwerte — deshalb hier und nicht
     * im Code.
     */
    trip_max_cycle_days: { schema: z.number().int().min(1).max(30), default: 4 },
    trip_mid_layer_below_c: { schema: z.number().min(-20).max(30), default: 14 },
    trip_outer_layer_below_c: { schema: z.number().min(-20).max(30), default: 8 },
};
export const SETTING_KEYS = Object.keys(SETTINGS_SPEC);
export function defaultSettings() {
    const out = {};
    for (const key of SETTING_KEYS) {
        out[key] = structuredClone(SETTINGS_SPEC[key].default);
    }
    return out;
}
export function parseSetting(key, raw) {
    return SETTINGS_SPEC[key].schema.parse(raw);
}
//# sourceMappingURL=settings.js.map